import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { Container, Header } from '@components';
import { InventoryProductCard } from '@components/cards';
import { PLACEHOLDERS, SafeImage } from '@components/image';
import { useInView } from 'framer-motion';
import { ChevronRight, Loader, PackageOpen } from 'lucide-react';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  getCollection,
  getCollectionProducts,
  getCollections,
} from '@api/collections';
import { getStore } from '@api/store';
import { Navbar } from '@components/navbar';
import { TCollection, TCollectionItem, TStore } from '@components/types';
import { getSafeUrl, sanityIoImageLoader } from '@core/utils';

const SORTING_OPTIONS = [
  { value: 'relevence', label: 'Relevence' },
  { value: 'popularity', label: 'Popularity' },
  { value: 'rating', label: 'Rating: High to Low' },
];

interface CollectionsProps {
  initialStore: TStore;
  initialCollection: TCollection;
}

export default function Collections({
  initialStore,
  initialCollection,
}: CollectionsProps) {
  const [products, setProducts] = useState<TCollectionItem[]>([]);
  const [collections, setCollections] = useState<TCollection[]>([]);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isData, setIsData] = useState(true);
  const PAGE_LIMIT = 10;

  const [orderingOn, setOrderingOn] = useState<string | null>(
    SORTING_OPTIONS[0].value
  );
  const [collectionId, setCollectionId] = useState<number>(1);

  const searchParams = useSearchParams();

  const ref = useRef(null);
  const isInView = useInView(ref);

  function handleCollectionChange(id: number) {
    setCollectionId(id);
    setProducts([]);
    setIsData(true);
    setSkip(0);
  }

  function handleSortingChange(option: string) {
    if (orderingOn === option) setOrderingOn(null);
    else setOrderingOn(option);
    setProducts([]);
    setIsData(true);
    setSkip(0);
  }

  function handleClearFilters() {
    setOrderingOn(null);
    setProducts([]);
    setIsData(true);
    setSkip(0);
  }

  async function fetchCollectionProducts(
    collectionId: number,
    orderingOn: string,
    skip: number
  ) {
    setIsLoading(true);
    try {
      const data: TCollectionItem[] = await getCollectionProducts(
        collectionId,
        orderingOn,
        skip,
        PAGE_LIMIT
      );

      if (skip === 0) {
        setProducts(data);
      } else {
        if (data.length) {
          setProducts([...products, ...data]);
        } else {
          setIsData(false);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchCollections() {
    const collections: TCollection[] = await getCollections(initialStore?.id);
    setCollections(collections);
  }

  useMemo(() => {
    if (searchParams.get('collection_id')) {
      setSkip(0);
      setCollectionId(+searchParams.get('collection_id'));
    }
  }, [searchParams]);

  useEffect(() => {
    fetchCollectionProducts(collectionId, orderingOn, skip);
  }, [collectionId, orderingOn, skip]);

  useEffect(() => {
    if (isInView && isData && isLoading === false) {
      setSkip((prevSkip) => prevSkip + PAGE_LIMIT);
      setIsLoading(true);
    }
  }, [isInView, isData, isLoading]);

  useEffect(() => {
    fetchCollections();
  }, []);

  const storeHref = `/store/${initialStore?.id}/${getSafeUrl(initialStore?.name || 'store')}`;
  const activeCollection = collections?.find((c) => c.id == collectionId) || initialCollection;

  return (
    <Container>
      <Head>
        <title>
          {initialCollection?.title} - {initialStore?.name}
        </title>
        <meta
          name="description"
          content={`Check out ${initialCollection?.title} by ${initialStore.name}. Browse and order products today from your nearby store.`}
        />
        <meta
          name="og:title"
          content={`${initialCollection?.title} - ${initialStore?.name}`}
        />
        <meta
          name="og:description"
          content={`Check out ${initialCollection?.title} by ${initialStore.name}. Browse and order products today from your nearby store.`}
        />
        <meta
          name="og:image"
          content={activeCollection?.image_url || initialStore?.image}
        />
      </Head>

      <Header backText="All Collections" />

      <main className="min-h-screen max-w-[1240px] pb-10 w-full mx-auto font-manrope">
        <div className="hidden px-2 py-8 md:flex items-center text-base font-manrope">
          <Breadcrumb className="text-base text-neutral-400 font-medium font-manrope list-none text-breadcrumbs">
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink href={storeHref}>{initialStore?.name}</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink className="text-neutral-800 capitalize font-semibold">
                {activeCollection?.title || 'Collection'}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>

        <div className="flex flex-col gap-3 md:gap-4 px-2 pt-2 md:pt-0">
          {/* Hero */}
          <section className="flex items-center gap-3 md:gap-4 bg-white border border-solid border-neutral-200/70 shadow-sm rounded-2xl p-3 md:p-5">
            <SafeImage
              loader={sanityIoImageLoader}
              src={activeCollection?.image_url || initialStore?.image}
              fallback={PLACEHOLDERS.store}
              alt={activeCollection?.title || 'Collection'}
              width="160"
              height="160"
              priority
              className="w-16 h-16 md:w-24 md:h-24 object-cover rounded-xl bg-neutral-100 flex-shrink-0"
            />
            <div className="min-w-0 flex-1 flex flex-col gap-1">
              <h1 className="m-0 text-lg md:text-2xl font-bold text-neutral-900 leading-snug capitalize line-clamp-2">
                {activeCollection?.title || 'Collection'}
              </h1>
              {activeCollection?.subtitle && (
                <p className="m-0 text-sm text-neutral-500 font-medium line-clamp-2">{activeCollection.subtitle}</p>
              )}
              <Link href={storeHref} prefetch={false} className="inline-flex items-center gap-0.5 text-sm font-semibold text-brand-500 no-underline">
                by {initialStore?.name} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </section>

          {/* Collection switcher + sort */}
          <div className="sticky top-0 z-30 -mx-2 px-2 py-2 bg-white/95 backdrop-blur flex flex-col gap-2">
            {collections?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {collections.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => handleCollectionChange(c.id)}
                    className={`flex-shrink-0 h-8 px-3.5 rounded-full border border-solid text-sm font-semibold capitalize whitespace-nowrap cursor-pointer transition-colors ${
                      c.id === collectionId
                        ? 'bg-business-500 border-business-500 text-white'
                        : 'bg-white border-neutral-200 text-neutral-600 hover:border-business-500/40'
                    }`}
                  >
                    {c.title}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
              <span className="text-xs font-bold uppercase tracking-wide text-neutral-400 flex-shrink-0">Sort</span>
              {SORTING_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSortingChange(option.value)}
                  className={`flex-shrink-0 h-7 px-3 rounded-full border-0 text-xs font-semibold cursor-pointer transition-colors ${
                    orderingOn === option.value
                      ? 'bg-neutral-900 text-white'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
              <span className="ml-auto text-xs font-semibold text-neutral-400 flex-shrink-0">
                {products?.length || 0} {products?.length === 1 ? 'item' : 'items'}
              </span>
            </div>
          </div>

          {/* Products */}
          <section className="flex flex-col gap-3">
            {isLoading && products.length === 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-3">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-32 rounded-2xl bg-neutral-100 animate-pulse" />
                ))}
              </div>
            )}

            {!isLoading && products.length === 0 && (
              <div className="flex flex-col items-center gap-2 bg-white border border-solid border-neutral-200/70 rounded-2xl py-10 text-center">
                <span className="w-12 h-12 rounded-full bg-neutral-100 text-neutral-400 flex items-center justify-center">
                  <PackageOpen className="w-6 h-6" />
                </span>
                <p className="m-0 text-sm font-semibold text-neutral-500">No items in this collection yet</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-3">
              {products?.map((item) => (
                <InventoryProductCard key={item?.id} element={item?.inventory} />
              ))}
            </div>

            {isLoading && products.length > 0 && (
              <div className="py-4 flex justify-center">
                <Loader className="w-7 h-7 text-neutral-400 animate-spin" />
              </div>
            )}
            <div ref={ref} className="w-full h-10" />
          </section>
        </div>
        <Navbar />
      </main>
    </Container>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { store_id, collection_id } = context.query;

  const initialStore = await getStore(+store_id);
  const initialCollection = await getCollection(+collection_id);

  return {
    props: {
      initialStore,
      initialCollection,
    },
  };
};
